import type { Entry, Loan } from '@taler/api-client'
import { addMoney, type LocalDate, type Money, requireCurrencyCode, zeroMoney } from '@taler/domain'

export interface LoanPaymentRow {
  readonly entryId: string
  readonly date: LocalDate
  readonly amount: Money
  readonly principalPortion: Money
  readonly interestPortion: Money
  readonly overpaymentPortion: Money
}

/**
 * There is no `loans_payments` list operation on the wire - a loan's own principal
 * account is dedicated to it, so its payment history is every entry that touches that
 * account, read through the entries repository already responsible for entries. The
 * interest posting is absent, not zero, on a payment that was pure principal - a real
 * case once accrued interest reaches zero, not a missing-data fallback.
 */
/** A drawdown credits the principal account (negative); a payment debits it. */
function isDrawdown(entry: Entry, loan: Loan): boolean {
  const principal = entry.postings.find((posting) => posting.accountId === loan.principalAccountId)
  return principal !== undefined && principal.amount.units < 0n
}

export function paymentRowsFromEntries(entries: readonly Entry[], loan: Loan): readonly LoanPaymentRow[] {
  const currency = requireCurrencyCode(loan.currency)
  return entries
    .filter((entry) => !isDrawdown(entry, loan))
    .map((entry) => {
      const principal = entry.postings.find((posting) => posting.accountId === loan.principalAccountId)
      const interest = entry.postings.find(
        (posting) =>
          posting.accountId === loan.interestExpenseAccountId ||
          posting.accountId === loan.interestPayableAccountId,
      )
      const overpayment = entry.postings.find((posting) => posting.accountId === loan.overpaymentAccountId)
      const principalPortion = principal?.amount ?? zeroMoney(currency)
      const interestPortion = interest?.amount ?? zeroMoney(currency)
      const overpaymentPortion = overpayment?.amount ?? zeroMoney(currency)
      return {
        entryId: entry.id,
        date: entry.date,
        amount: addMoney(addMoney(principalPortion, interestPortion), overpaymentPortion),
        principalPortion,
        interestPortion,
        overpaymentPortion,
      }
    })
}
